import styled from "@emotion/styled"
import React from "react"
import { Flex, Image } from "rebass"
import Typography from "../typography"
import HeapsLogo from "../../assets/images/heaps-logo.svg"
import { desktopContainer, mobileContainer } from "../../theme"
import { LanguagePickerSelector } from "../language-picker/LanguagePickerSelector"
import { FooterLink } from "./FooterLink"
import { BodyTextRegular } from "../typography/text"
import { useTheme } from "@emotion/react"
import { useTranslation } from "react-i18next"
import { translationKeys } from "../../util/translationKeys"
import moment from "moment"
import { routes } from "../../routes"
import {
  LEGAL_LAST_UPDATED_MARKETING,
  LEGAL_LAST_UPDATED_PRIVACY,
  LEGAL_LAST_UPDATED_TERMS,
  LEGAL_VERSION_MARKETING,
  LEGAL_VERSION_PRIVACY,
  LEGAL_VERSION_TERMS,
  PARTNER_ADDRESS_AND_HOUSE_NUMBER,
  PARTNER_CITY,
  PARTNER_COMPANY_NAME,
  PARTNER_DATA_PROCESSORS,
  PARTNER_PARTNERS,
  PARTNER_POST_CODE,
  PARTNER_SHORT_NAME,
  PARTNER_SUPPORT_EMAIL,
  PARTNER_VAT,
  language,
} from "../../util/legal-general"

const Logo = process.env.GATSBY_LOGO || ""

const Container = styled(Flex)`
  ${Typography.Base};
`

const FooterContainer = styled(Flex)``

const SUPPORT_CHAT = process.env.GATSBY_SUPPORT_CHAT || ""
const SUPPORT_EMAIL = process.env.GATSBY_SUPPORT_EMAIL || ""
const SUPPORT_WEB = process.env.GATSBY_SUPPORT_WEB || ""
const GATSBY_COOKIES = process.env.GATSBY_COOKIES || ""
const GATSBY_TERMS = process.env.GATSBY_TERMS || ""
const GATSBY_PRIVACY = process.env.GATSBY_PRIVACY || ""
const EXTERNAL_REDIRECT_TEXT =
  process.env.GATSBY_EXTERNAL_REDIRECT_TEXT || "Catering & events"
const EXTERNAL_REDIRECT_LINK = process.env.GATSBY_EXTERNAL_REDIRECT_LINK || ""
const APP_VERSION = process.env.GATSBY_APP_VERSION
const linkToExternalLegalPages =
  !language ||
  !PARTNER_SHORT_NAME ||
  !PARTNER_COMPANY_NAME ||
  !PARTNER_VAT ||
  !PARTNER_ADDRESS_AND_HOUSE_NUMBER ||
  !PARTNER_POST_CODE ||
  !PARTNER_CITY ||
  !PARTNER_SUPPORT_EMAIL ||
  !PARTNER_DATA_PROCESSORS ||
  !PARTNER_PARTNERS ||
  !LEGAL_VERSION_TERMS ||
  !LEGAL_LAST_UPDATED_TERMS ||
  !LEGAL_VERSION_PRIVACY ||
  !LEGAL_LAST_UPDATED_PRIVACY ||
  !LEGAL_VERSION_MARKETING ||
  !LEGAL_LAST_UPDATED_MARKETING

const Footer = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const currentYear = moment().format("YYYY")

  return (
    <Flex
      sx={{
        width: "100%",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#f3f8fd",
      }}
    >
      <Container
        flexDirection="column"
        width="100%"
        mb={["48px", 0]}
        px={["20px", "100px"]}
        maxWidth="1300px"
        height="100%"
      >
        <div css={mobileContainer}>
          <FooterContainer
            height={["100%", "150px"]}
            justifyContent={["space-between", "center"]}
            alignItems="center"
            flexDirection="column"
          >
            <Flex
              flexDirection={["column", "row"]}
              alignItems={[, "center"]}
              width={["100%", "100%"]}
              justifyContent={[, "space-between"]}
            >
              <Flex
                mb={[2, 0]}
                width={["350px", "100%"]}
                maxHeight={[, "60px"]}
                alignItems={["start", "end"]}
                flexDirection={["column", "row-reverse"]}
              >
                <Image
                  src={Logo}
                  sx={{
                    height: "20px",
                    marginBottom: "32px",
                    marginTop: "48px",
                  }}
                />
                <Flex
                  justifyContent={["flex-end"]}
                  width="100%"
                  flexDirection={["column", "row"]}
                >
                  {EXTERNAL_REDIRECT_LINK !== "" && (
                    <FooterLink href={EXTERNAL_REDIRECT_LINK}>
                      {EXTERNAL_REDIRECT_TEXT}
                    </FooterLink>
                  )}
                  {SUPPORT_WEB !== "" && (
                    <FooterLink href={SUPPORT_WEB}>
                      {t(translationKeys.comboProfileSupportWeb)}
                    </FooterLink>
                  )}
                  {SUPPORT_CHAT !== "" && (
                    <FooterLink href={SUPPORT_CHAT}>
                      {t(translationKeys.comboProfileSupport)}
                    </FooterLink>
                  )}
                  {SUPPORT_EMAIL !== "" && (
                    <FooterLink href={`mailto:${SUPPORT_EMAIL}`}>
                      {t(translationKeys.comboProfileSupportEmail)}
                    </FooterLink>
                  )}
                  <FooterLink
                    href={
                      linkToExternalLegalPages
                        ? GATSBY_COOKIES
                        : routes.legal.cookies
                    }
                  >
                    Cookies
                  </FooterLink>
                  <FooterLink
                    href={
                      linkToExternalLegalPages
                        ? GATSBY_TERMS
                        : routes.legal.terms
                    }
                  >
                    Terms and conditions
                  </FooterLink>
                  <FooterLink
                    href={
                      linkToExternalLegalPages
                        ? GATSBY_PRIVACY
                        : routes.legal.privacy
                    }
                  >
                    Privacy policy
                  </FooterLink>
                  <LanguagePickerSelector />
                </Flex>
                <Flex
                  flexDirection={["column", "column"]}
                  justifyContent="center"
                  height="100%"
                  minWidth={[, "240px"]}
                >
                  <Flex
                    sx={{
                      height: "20px",
                      marginTop: ["48px", "48px", 0],
                      marginBottom: ["16px"],
                      maxWidth: "110px",
                    }}
                  >
                    <HeapsLogo />
                  </Flex>
                  <BodyTextRegular color={theme.colors.primary}>
                    Powered by HeapsGo. © 2020-{currentYear}.
                  </BodyTextRegular>
                  <BodyTextRegular color={theme.colors.primary}>
                    {`Version: ${APP_VERSION?.slice(
                      0,
                      5
                    )?.toLocaleUpperCase()}`}
                  </BodyTextRegular>
                </Flex>
              </Flex>
            </Flex>
          </FooterContainer>
        </div>
        <div css={desktopContainer}>
          <FooterContainer
            height={["150px"]}
            alignItems="center"
            flexDirection="column"
          >
            <Flex
              flexDirection={["column"]}
              width="100%"
              justifyContent={["space-between"]}
            >
              <Flex
                flexDirection={["row"]}
                height="100%"
                width="100%"
                sx={{ marginBottom: "16px", marginTop: "48px" }}
                justifyContent="space-between"
                alignItems="center"
              >
                <Flex sx={{ height: "20px" }}>
                  <HeapsLogo />
                </Flex>
                <Image
                  src={Logo}
                  sx={{
                    height: "20px",
                  }}
                />
              </Flex>
              <Flex
                justifyContent={["space-between"]}
                width="100%"
                flexDirection={["row"]}
              >
                <Flex>
                  <BodyTextRegular color={theme.colors.primary}>
                    Powered by HeapsGo. © 2020-{currentYear}.
                  </BodyTextRegular>
                </Flex>
                <Flex flexDirection="row">
                  <LanguagePickerSelector />
                  {EXTERNAL_REDIRECT_LINK !== "" && (
                    <FooterLink href={EXTERNAL_REDIRECT_LINK}>
                      {EXTERNAL_REDIRECT_TEXT}
                    </FooterLink>
                  )}
                  {SUPPORT_WEB !== "" && (
                    <FooterLink href={SUPPORT_WEB}>
                      {t(translationKeys.comboProfileSupportWeb)}
                    </FooterLink>
                  )}
                  {SUPPORT_CHAT !== "" && (
                    <FooterLink href={SUPPORT_CHAT}>
                      {t(translationKeys.comboProfileSupport)}
                    </FooterLink>
                  )}
                  {SUPPORT_EMAIL !== "" && (
                    <FooterLink href={`mailto:${SUPPORT_EMAIL}`}>
                      {t(translationKeys.comboProfileSupportEmail)}
                    </FooterLink>
                  )}
                  <FooterLink
                    href={
                      linkToExternalLegalPages
                        ? GATSBY_COOKIES
                        : routes.legal.cookies
                    }
                    internalLink={linkToExternalLegalPages ? false : true}
                  >
                    {t(translationKeys.cookies)}
                  </FooterLink>
                  <FooterLink
                    href={
                      linkToExternalLegalPages
                        ? GATSBY_TERMS
                        : routes.legal.terms
                    }
                    internalLink={linkToExternalLegalPages ? false : true}
                  >
                    {t(translationKeys.webTerms)}
                  </FooterLink>
                  <FooterLink
                    href={
                      linkToExternalLegalPages
                        ? GATSBY_PRIVACY
                        : routes.legal.privacy
                    }
                    internalLink={linkToExternalLegalPages ? false : true}
                  >
                    {t(translationKeys.webPrivacy)}
                  </FooterLink>
                </Flex>
              </Flex>
              <BodyTextRegular color={theme.colors.primary}>
                {`Version: ${APP_VERSION?.slice(0, 5)?.toLocaleUpperCase()}`}
              </BodyTextRegular>
            </Flex>
          </FooterContainer>
        </div>
      </Container>
    </Flex>
  )
}

export default Footer
